import { FC, useMemo } from "react";
import { Flipped, Flipper } from "react-flip-toolkit";
import { UseFormReturn } from "react-hook-form";
import { makeStyles } from "tss-react/mui";
import { MiAlert, MiButton } from "@miplatform/components";
import { ButtonVariants } from "@miplatform/types";
import { mdiPlus } from "@mdi/js";
import { Box } from "@mui/material";
import { ConfigAccordionHeader } from "../../../components/ConfigAccordionHeader/ConfigAccordionHeader";
import { TRAYS_LIMIT } from "../../ConfigCalendarSetList.constants";
import { ICalendarSetData } from "../../ConfigCalendarSetList.types";
import { CalendarTrayCard } from "./CalendarTrayCard";

export const CalendarTrayListTabView: FC<CalendarTrayListTabViewProps> = ({
  form,
  extraTabProps: { isReadOnly, onAddTray, onTabChange } = {},
}: CalendarTrayListTabViewProps) => {
  const { classes } = useStyles();
  const { setValue, watch } = form;
  const trays = watch("trays");
  const calendarConfigs = watch("calendarConfigs");
  const sortedTrays = useMemo(
    () => [...trays].sort((a, b) => a.displayOrder - b.displayOrder),
    [trays]
  );

  const swapDisplayOrders = (trayAIndex: number, trayBIndex: number) => {
    const newTrays = [...sortedTrays];
    const tempDisplayOrder = newTrays[trayAIndex].displayOrder;
    newTrays[trayAIndex].displayOrder = newTrays[trayBIndex].displayOrder;
    newTrays[trayBIndex].displayOrder = tempDisplayOrder;

    return newTrays;
  };

  const handleReorderUp = (trayId: string) => {
    const index = sortedTrays.findIndex((tray) => tray.id === trayId);

    if (index !== -1 && index > 0) {
      const newTrays = swapDisplayOrders(index, index - 1);

      setValue("trays", newTrays, { shouldDirty: true });
    }
  };

  const handleReorderDown = (trayId: string) => {
    const index = sortedTrays.findIndex((tray) => tray.id === trayId);

    if (index !== -1 && index < sortedTrays.length - 1) {
      const newTrays = swapDisplayOrders(index, index + 1);

      setValue("trays", newTrays, { shouldDirty: true });
    }
  };

  const handleDeleteTray = (trayId: string) => {
    const newTrays = trays.filter((tray) => tray.id !== trayId);
    setValue("trays", newTrays, { shouldDirty: true });
    const calendarConfigAttachedToTray = calendarConfigs.findIndex(
      (config) => config.attachedTray?.id === trayId
    );

    if (calendarConfigAttachedToTray !== -1) {
      setValue(
        `calendarConfigs.${calendarConfigAttachedToTray}.attachedTray`,
        undefined,
        { shouldDirty: true }
      );
    }
  };

  const handleEditTray = (trayId: string) => {
    onTabChange?.(trayId);
  };

  return (
    <ConfigAccordionHeader title="Trays">
      <Flipper
        flipKey={sortedTrays.map((tray) => tray.id).join("-")}
        className={classes.list}
      >
        {sortedTrays.map((tray, index) => (
          <Flipped key={tray.id} flipId={tray.id}>
            {(flipppedProps) => (
              <div {...flipppedProps}>
                <CalendarTrayCard
                  tray={tray}
                  isReadOnly={isReadOnly}
                  onDeleteClick={handleDeleteTray}
                  onEditClick={handleEditTray}
                  onReorderUp={handleReorderUp}
                  onReorderDown={handleReorderDown}
                  isFirstInList={index === 0}
                  isLastInList={index === sortedTrays.length - 1}
                />
              </div>
            )}
          </Flipped>
        ))}
      </Flipper>
      <MiAlert
        description="Configure the work that will append to the bottom of the calendar to be dispatched."
        sx={{ mt: 2, py: 0, px: 2 }}
      />
      {!isReadOnly && (
        <Box display="flex" justifyContent="flex-end" mt={2}>
          <MiButton
            startIcon={{ path: mdiPlus }}
            variant={ButtonVariants.Contained}
            onClick={onAddTray}
            disabled={trays.length >= TRAYS_LIMIT}
            title="Add Custom Tray"
          />
        </Box>
      )}
    </ConfigAccordionHeader>
  );
};

interface CalendarTrayListTabViewProps {
  form: UseFormReturn<ICalendarSetData>;
  extraTabProps?: {
    isReadOnly?: boolean;
    onAddTray?: () => void;
    onTabChange?: (trayId: string) => void;
  };
}

const useStyles = makeStyles()((theme) => ({
  list: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
  },
}));
